import React, { useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useIsMobile } from '@shared/hooks';
import { useNavigate } from 'react-router';
// MUI Menu components - kept as dynamon-as/ui does not have Menu/Dropdown yet
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import LogoutIcon from '@mui/icons-material/Logout';
import TranslateIcon from '@mui/icons-material/Translate';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { Button } from '@dynamon-as/ui';
import type { OrgLite } from '@shared/types';
import { useI18n, LANGUAGE_CODES, SUPPORTED_LANGUAGES } from '@shared/i18n';
import styles from './Header.module.css';

interface HeaderProps {
    org?: OrgLite;
}

const Header: React.FC<HeaderProps> = ({ org }): React.ReactElement => {
    const { user, isAuthenticated, logout } = useAuth0();
    const navigate = useNavigate();
    const isMobile = useIsMobile();
    const { language, changeLanguage, t } = useI18n();

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [languageMenuAnchor, setLanguageMenuAnchor] =
        useState<null | HTMLElement>(null);
    const menuOpen = Boolean(anchorEl);
    const languageMenuOpen = Boolean(languageMenuAnchor);

    // Derived display name from Auth0 user
    const displayName = useMemo((): string => {
        return user
            ? (user.email ?? user.nickname ?? user.name ?? user.sub ?? '')
            : '';
    }, [user]);

    const handleLogout = async (): Promise<void> => {
        try {
            await logout({
                logoutParams: {
                    returnTo: window.location.origin,
                },
            });
            navigate('/');
        } catch (error) {
            console.error('Logout error:', error);
        }
    };

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>): void => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = (): void => {
        setAnchorEl(null);
        setLanguageMenuAnchor(null);
    };

    const handleLanguageMenuOpen = (
        event: React.MouseEvent<HTMLElement>,
    ): void => {
        setLanguageMenuAnchor(event.currentTarget);
    };

    const handleLanguageChange = (
        lang: (typeof SUPPORTED_LANGUAGES)[keyof typeof SUPPORTED_LANGUAGES],
    ): void => {
        changeLanguage(lang);
        handleMenuClose();
    };

    return (
        <header className={styles.header}>
            <div className={styles.left}>
                {isMobile && (
                    <img
                        src="/images/logo-orange.svg"
                        alt="Dynamon"
                        className={styles.logoImage}
                    />
                )}
                <span className={styles.orgName}>{org?.name}</span>
            </div>
            <div className={styles.right}>
                {import.meta.env.VITE_DEV_MODE === 'true' && (
                    <Button
                        Icon={NotificationsIcon}
                        onClick={(): void => {
                            void navigate('/notifications');
                        }}
                        className={styles.notificationsButton}
                        aria-label={t('header.notifications.ariaLabel')}
                        variant="flat"
                        rounded
                        size="small"
                    />
                )}
                <Button
                    Icon={MoreHorizIcon}
                    onClick={handleMenuOpen}
                    aria-label={t('header.menu.ariaLabel')}
                    variant="flat"
                    rounded
                    size="small"
                />

                {/* Main menu */}
                <Menu
                    anchorEl={anchorEl}
                    open={menuOpen}
                    onClose={handleMenuClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    className={styles.menu}
                >
                    {isAuthenticated && displayName && (
                        <span className={styles.menuUserName}>
                            {displayName}
                        </span>
                    )}
                    {isAuthenticated && displayName && (
                        <hr className={styles.menuDivider} />
                    )}

                    {/* Language selector menu item */}
                    <MenuItem
                        onClick={handleLanguageMenuOpen}
                        className={styles.menuItem}
                    >
                        <span className={styles.menuItemIcon}>
                            <TranslateIcon fontSize="small" />
                        </span>
                        <span className={styles.menuItemText}>
                            {t('header.menu.selectLanguage')}
                        </span>
                    </MenuItem>

                    {/* Language submenu */}
                    <Menu
                        anchorEl={languageMenuAnchor}
                        open={languageMenuOpen}
                        onClose={handleMenuClose}
                        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                        className={styles.languageMenu}
                        disablePortal
                    >
                        {Object.values(SUPPORTED_LANGUAGES).map(
                            (lang): React.ReactElement => (
                                <MenuItem
                                    key={lang}
                                    onClick={(): void =>
                                        handleLanguageChange(lang)
                                    }
                                    selected={language === lang}
                                    className={styles.languageMenuItem}
                                >
                                    <span className={styles.languageCode}>
                                        {
                                            LANGUAGE_CODES[
                                                lang as keyof typeof LANGUAGE_CODES
                                            ]
                                        }
                                    </span>
                                </MenuItem>
                            ),
                        )}
                    </Menu>

                    <MenuItem
                        onClick={handleLogout}
                        className={styles.menuItem}
                    >
                        <span className={styles.menuItemIcon}>
                            <LogoutIcon fontSize="small" />
                        </span>
                        <span className={styles.menuItemText}>
                            {t('header.menu.logout')}
                        </span>
                    </MenuItem>
                </Menu>
            </div>
        </header>
    );
};

export default Header;
